<template>
  <div v-if="props.show" class="modal fade show custom-modal-backdrop" aria-modal="true" role="dialog" style="display: block;">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="row">
          <div class="col-12">
            <div class="d-flex justify-content-between px-1 my-1 align-items-center">
              <h1 class="mb-0">Scheduler</h1>
              <button @click="onCancel" type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
          </div>
        </div>

        <hr class="mt-0 mb-0">

        <div class="px-2">
          <ul class="nav nav-tabs">
            <li v-for="tab in tabs" :key="tab.id" class="nav-item">
              <a
                :class="['nav-link', { active: activeTab.id == tab.id }]"
                @click="setActiveTab(tab)"
              >
                {{ tab.title }}
              </a>
            </li>
          </ul>
        </div>

        <div class="col-12 max-modal-height overflow-y-auto">
          <component
            :is="tabComponents[activeTab.id]"
            :startDate="startDate"
            :endDate="endDate"
            :editData="computedEditData"
            :tagList="tagList"
            :priorityList="priorityList"
            @handleSubmit="onSave"
            ref="childRef"
          />
        </div>

        <div class="row justify-content-end p-1 border-top">
          <div class="col-md-4 col-12 text-end">
            <button class="btn btn-primary px-3" :disabled="loading" @click="onSave">
              <i class="fa fa-save"></i> 
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              Save All & Update
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, inject, watch, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import Task from "./components/Task.vue";
import Event from "./components/Event.vue";
import handleActivities from "@/services/modules/procurement/activity";
import { getCurrentDateTime } from '@/services/utils/global.js';
import useDate from '@/services/utils/day';

const props = defineProps({
  show: Boolean,
  item: Object
});

const emit = defineEmits(['update:show', 'success']);
const route = useRoute();
const store = useStore();
const showSuccess = inject('showSuccess');
const showError = inject('showError');

const activeContactProfile = inject('activeContactProfile');
const tagList = inject('tagList', ref([]));
const priorityList = inject('priorityList', ref([]));

const { storeActivityWorkStatus } = handleActivities();
const { formatDateTime, formatToDate } = useDate();

const loading = ref(false);
const childRef = ref(null);

const startDate = ref(getCurrentDateTime());
const endDate = ref(getCurrentDateTime(null, { hours: 1 }));

const tabs = [
  { id: 'task', title: 'Task' },
  { id: 'event', title: 'Event' },
];

const tabComponents = {
  'task': Task,
  'event': Event
};

const activeTab = ref(tabs[0]);

const setActiveTab = (tab) => {
  activeTab.value = tab;
};

const onCancel = () => {
  emit('update:show', false);
};

// Construct editData to pre-fill the child components
const computedEditData = computed(() => {
  if (!props.item || !activeContactProfile || !activeContactProfile.value) return {};

  return {
    short_description: props.item.name,
    values: [
      { 
        field_key: 'project_id', 
        field_value: { id: props.item.project_id, name: props.item.project_name },
        field_value_id: props.item.project_id
      },
      { 
        field_key: 'project_activity_id', 
        field_value: { id: props.item.id, name: props.item.name },
        field_value_id: props.item.id
      },
      { 
        field_key: 'assigned_to', 
        field_value: { id: activeContactProfile.value.id, name: activeContactProfile.value.full_name },
        field_value_id: activeContactProfile.value.id
      }
    ]
  };
});

const onSave = async () => {
  if (!childRef.value) return;

  loading.value = true;
  try {
    const data = childRef.value.getFormData();
    const workStatusData = childRef.value.getWorkStatusFormData();
    let companyId = route.params.companyId;

    // ===== 1. Activity Log fields (exact same format as TaskEventScheduler handleSave) =====
    const formattedData = {
      short_description: data.title,
      start_time: data.start_time ? formatDateTime(data.start_time) : null,
      end_time: data.end_time ? formatDateTime(data.end_time) : null,
      date: data.start_time ? formatToDate(data.start_time) : null,
      assigned_to: data.assign_to ? data.assign_to.id : null,
      report_to: data.report_to ? data.report_to.id : null,
      area_id: data.area_id ? data.area_id.id : null,
      participant_ids: data.participants ? data.participants.map(p => p.id) : [],
      activity_setup_id: data.activity_setup_id || null,
      tags: data.tag ? data.tag.map(t => t.id) : [],
      project_id: data.project ? data.project.id : null,
      project_activity_id: data.project_activity ? data.project_activity.id : null,
      full_description: data.description,
      type: activeTab.value.id,
      client_id: data.client ? data.client.id : null,
      priority_id: data.priority ? data.priority.id : null,
      deal_id: data.deal ? data.deal.id : null,
      route_name: 'task-calendar',
      company_id: companyId
    };

    // Build FormData exactly like TaskEventScheduler does
    const formData = new FormData();
    Object.entries(formattedData).forEach(([key, value]) => {
      Array.isArray(value)
          ? value.forEach((item, i) => formData.append(`${key}[${i}]`, item))
          : formData.append(key, (value != null) ? value : '');
    });

    // Handle Attachments (exact same as TaskEventScheduler handleAttachments)
    const attachments = data.attachments;
    if (attachments && attachments.length) {
      const previousFileIds = attachments
        .filter(attachment => typeof attachment === 'object' && attachment.hasOwnProperty('id'))
        .map(attachment => attachment.id);

      attachments.forEach((attachment, index) => {
        if (typeof attachment !== 'object' || !attachment.hasOwnProperty('id')) {
          formData.append(`attachments[${index}]`, attachment);
        }
      });

      if (previousFileIds.length) {
        formData.append('previous_file_ids', JSON.stringify(previousFileIds));
      }
    }

    // ===== 2. Work Status fields (exact same format as TaskEventScheduler handleWorkStatusSave) =====
    formData.append('work_status[area_id]', (workStatusData.area_id && workStatusData.area_id.id) ? workStatusData.area_id.id : '');
    formData.append('work_status[hours]', workStatusData.hours || '');
    formData.append('work_status[date]', workStatusData.date || '');
    formData.append('work_status[conveyance]', workStatusData.conveyance || '');
    formData.append('work_status[description]', workStatusData.description || '');

    const response = await storeActivityWorkStatus(formData);
    if (!response.status) {
      showError(response.message || "Failed to save");
      return;
    }
    showSuccess(response.message || "Work task saved successfully");
    emit('success', null);
    onCancel();
  } catch (error) {
    showError("An error occurred during save");
    console.error(error);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  // Global lists now provided from parent Update.vue
});

watch(() => props.show, (newVal) => {
  if (newVal) {
    startDate.value = getCurrentDateTime();
    endDate.value = getCurrentDateTime(null, { hours: 1 });
  }
});
</script>

<style scoped>
.custom-modal-backdrop {
  display: block; 
  background: rgba(28, 29, 28, 0.2);
  z-index: 1060;
}
.max-modal-height {
  max-height: 70vh;
}
.overflow-y-auto {
  overflow-y: auto;
}
.nav-tabs .nav-link {
  cursor: pointer;
}
</style>

